import React, { useEffect, useState, useRef } from 'react';
import { OMSAUTH, OMSPROFILE } from '../../API';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useDispatch, useSelector } from 'react-redux';
import { resendEmail, resetNotification } from './authSlice';
import Notification from '../../pages/General/Notification';
import Loader from '../../pages/General/Loader';

// ─── Enterprise design tokens ────────────────────────────────────────────────
const tokens = {
  navy:          "#0A1628",
  accent:        "#2563EB",
  accentHover:   "#1D4ED8",
  border:        "#E2E8F0",
  surface:       "#F8FAFC",
  white:         "#FFFFFF",
  textPrimary:   "#0F172A",
  textSecondary: "#475569",
};

// ─── Global style injection ───────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .verify-root {
      display: flex;
      min-height: 100vh;
      align-items: center;
      justify-content: center;
      font-family: 'Inter', system-ui, sans-serif;
      background: ${tokens.surface};
      position: relative;
      overflow: hidden;
      padding: 24px 16px;
    }

    /* Subtle background accent gradients to match your auth workspace */
    .verify-root::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(ellipse 600px 500px at -100px 120%, #2563EB15 0%, transparent 70%),
                  radial-gradient(ellipse 400px 400px at 110% -10%, #2563EB10 0%, transparent 70%);
      pointer-events: none;
    }

    .verify-card {
      width: 100%;
      max-width: 460px;
      background: ${tokens.white} !important;
      border: 1px solid ${tokens.border} !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
      padding: 48px 36px !important;
      text-align: center;
      position: relative;
      z-index: 1;
    }

    .verify-icon-wrapper {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: rgba(37, 99, 235, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
    }

    .verify-icon-wrapper img {
      width: 36px;
      height: 36px;
      object-fit: contain;
    }

    .verify-title {
      font-size: 24px !important;
      font-weight: 700 !important;
      color: ${tokens.textPrimary} !important;
      margin-bottom: 12px !important;
    }

    .verify-description {
      font-size: 14px !important;
      line-height: 1.6 !important;
      color: ${tokens.textSecondary} !important;
      margin-bottom: 28px !important;
    }

    .verify-email-badge {
      display: inline-block;
      background: ${tokens.surface};
      border: 1px solid ${tokens.border};
      color: ${tokens.textPrimary};
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 6px;
      word-break: break-all;
    }

    .verify-footer-actions {
      font-size: 13px !important;
      color: ${tokens.textSecondary} !important;
      border-top: 1px solid ${tokens.border};
      padding-top: 20px;
      margin-top: 4px;
      line-height: 1.6;
    }

    .verify-link-trigger {
      color: ${tokens.accent};
      font-weight: 600;
      cursor: pointer;
      text-decoration: none;
      transition: color 0.15s ease;
    }

    .verify-link-trigger:hover {
      color: ${tokens.accentHover};
      text-decoration: underline;
    }
  `}</style>
);

// ─── Component ───────────────────────────────────────────────────────────────
const UserVerifyPage = () => {
    const auth = useSelector(state => state.auth);
    const profile = useSelector(state => state.auth.profile);
    console.log(auth);
    const dispatch = useDispatch();

    const handleRequest = () => {
        dispatch(resendEmail(profile.id, profile.email));
    }

    const logoutRegister = () => {
        console.log("Logout");
        window.location = process.env.REACT_APP_API_DOMAIN + "/auth/logout/register";
    }

    return (
        <>
            <GlobalStyles />

            {auth && auth.resentEmailloading === true && (
                <Loader />
            )}

            {(auth && auth.notification && auth.notification.showMessage) && (
                <Notification
                    is_open={auth.notification.showMessage}
                    messageType={auth.notification.messageType}
                    messages={auth.notification.messageContent}
                    handleClose={() => dispatch(resetNotification())}
                />
            )}

            <div className="verify-root">
                <Paper className="verify-card" elevation={0} square={false}>
                    {/* Inbox Visual Wrapper */}
                    <div className="verify-icon-wrapper">
                        <img src="/assets/images/trial-form/inbox.png" alt="Inbox" />
                    </div>

                    {/* Headline Accent */}
                    <Typography variant="h5" component="h3" className="verify-title">
                        Check your inbox
                    </Typography>

                    {/* Core Context Detail */}
                    <Typography className="verify-description">
                        We've sent an email to <span className="verify-email-badge">{profile && profile.email}</span> with a link to complete your account setup.
                    </Typography>

                    {/* Action Link Subtext */}
                    <p className="verify-footer-actions">
                        Having trouble?{" "}
                        <span onClick={handleRequest} className="verify-link-trigger">
                            Resend email
                        </span>{" "}
                        or{" "}
                        <span onClick={logoutRegister} className="verify-link-trigger">
                            Try a different email
                        </span>
                    </p>
                </Paper>
            </div>
        </>
    );
};

export default UserVerifyPage;